import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../queries";
import type { ReconcileReport } from "../types";

type Item = {
  id: string;
  group: string;
  label: string;
  hint?: string;
  run: () => void | Promise<void>;
};

/**
 * The ⌘K command palette (docs/dashboard-ux-proposal.md): the real navigation for
 * drill-down.
 *
 * It is fuzzy over runs, spec sections, registry keys, policies and findings, and
 * it carries *verbs* — "compare last two regressions", "reconcile stale runs",
 * "new policy". Opening it with an empty query shows the verbs; typing switches to
 * search results. Everything is a URL, so a result is just a jump.
 */
export default function CommandPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [reconcile, setReconcile] = useState<ReconcileReport | null>(null);

  // Mounted only while open (the Layout renders it conditionally), so state
  // starts fresh each time and no open-reset effect is needed.
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(query.trim());
      setActive(0);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [query]);
  const searchQuery = useQuery({
    queryKey: ["palette-search", debounced],
    queryFn: () => api.search(debounced),
    enabled: debounced.length > 0,
  });

  const applyReconcile = useMutation({
    mutationFn: () => api.reconcileRuns({ apply: true, action: "flag" }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      onClose();
    },
    onError: () => setStatus("Reconcile failed."),
  });

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  const compareLastTwo = async () => {
    setStatus("Finding the last two regressions…");
    try {
      const page = await api.listRuns(new URLSearchParams({ scope_kind: "full_regression", limit: "2" }));
      const [newest, older] = page.items;
      if (!newest || !older) {
        setStatus("Need two full regressions to compare.");
        return;
      }
      go(`/runs/${newest.id}/compare/${older.id}`);
    } catch {
      setStatus("Could not load runs.");
    }
  };

  const previewReconcile = async () => {
    setStatus("Previewing…");
    try {
      setReconcile(await api.reconcileRuns({ apply: false, action: "flag" }));
      setStatus(null);
    } catch {
      setStatus("Reconcile preview failed.");
    }
  };

  const verbs = useMemo<Item[]>(
    () => [
      { id: "v-now", group: "Go to", label: "Now", run: () => go("/") },
      { id: "v-runs", group: "Go to", label: "Runs", run: () => go("/runs") },
      { id: "v-coverage", group: "Go to", label: "Coverage", run: () => go("/coverage") },
      { id: "v-triage", group: "Go to", label: "Triage", run: () => go("/triage") },
      { id: "v-exploration", group: "Go to", label: "Exploration", run: () => go("/exploration") },
      { id: "v-policies", group: "Go to", label: "Policies", run: () => go("/policies") },
      { id: "v-new-policy", group: "Actions", label: "New policy", run: () => go("/policies/new") },
      {
        id: "v-compare",
        group: "Actions",
        label: "Compare last two regressions",
        run: compareLastTwo,
      },
      { id: "v-reconcile", group: "Actions", label: "Reconcile stale runs", run: previewReconcile },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const items = useMemo<Item[]>(() => {
    const searchItems: Item[] = [];
    const data = searchQuery.data;
    if (data) {
      for (const r of data.runs) {
        searchItems.push({
          id: `run-${r.id}`,
          group: "Runs",
          label: `#${r.run_number} · ${r.scope_ref}`,
          hint: r.scope_kind.replaceAll("_", " "),
          run: () => go(`/runs/${r.id}`),
        });
      }
      for (const s of data.sections) {
        searchItems.push({
          id: `sec-${s.spec_section_id}`,
          group: "Sections",
          label: s.spec_section_id,
          hint: "coverage",
          run: () => go(`/coverage?section=${encodeURIComponent(s.spec_section_id)}`),
        });
      }
      for (const p of data.policies) {
        searchItems.push({
          id: `pol-${p.id}`,
          group: "Policies",
          label: `${p.name} @v${p.version}`,
          run: () => go(`/policies/${p.id}`),
        });
      }
      for (const f of data.findings) {
        searchItems.push({
          id: `fnd-${f.id}`,
          group: "Findings",
          label: f.description,
          hint: `run #${f.run_number} · ${f.finding_type}`,
          run: () => go("/exploration"),
        });
      }
      for (const k of data.registry) {
        searchItems.push({
          id: `reg-${k.catalog}-${k.key}`,
          group: "Registry",
          label: `${k.name} (${k.key})`,
          hint: k.catalog,
          run: () => go(`/coverage?catalog=${k.catalog}&key=${k.key}`),
        });
      }
    }
    if (!debounced) return verbs;
    const needle = debounced.toLowerCase();
    const matchingVerbs = verbs.filter((v) => v.label.toLowerCase().includes(needle));
    return [...matchingVerbs, ...searchItems];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, searchQuery.data, verbs]);

  const clampedActive = items.length === 0 ? 0 : Math.min(active, items.length - 1);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = items[clampedActive];
      if (item) void item.run();
    }
  };

  // Group headers are rendered inline, so walk the list and emit a header when
  // the group changes.
  let lastGroup = "";

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Command palette">
      <button type="button" className="absolute inset-0 bg-black/30" aria-label="Close" onClick={onClose} />
      <div className="relative mx-auto mt-24 w-[min(40rem,92vw)] rounded-box bg-base-100 shadow-xl">
        <input
          ref={inputRef}
          className="input input-lg w-full rounded-b-none border-0 border-b border-base-300 focus:outline-none"
          placeholder="Search runs, sections, policies, findings, registry… or a verb"
          aria-label="Search"
          aria-controls="palette-results"
          aria-activedescendant={items[clampedActive]?.id}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />

        <ul id="palette-results" role="listbox" className="max-h-[26rem] overflow-y-auto p-2">
          {reconcile && (
            <li className="rounded-lg bg-base-200 p-3 m-1">
              <p className="text-sm">
                {reconcile.counts.completed} to complete · {reconcile.counts.reduced} to reduce ·{" "}
                {reconcile.counts.flagged} stale · {reconcile.counts.skipped} waiting on humans
              </p>
              <button
                type="button"
                className="btn btn-xs btn-primary mt-2"
                disabled={applyReconcile.isPending}
                onClick={() => applyReconcile.mutate()}
              >
                {applyReconcile.isPending ? "Applying…" : "Apply"}
              </button>
            </li>
          )}

          {searchQuery.isFetching && <li className="px-3 py-2 text-xs opacity-50">Searching…</li>}

          {items.map((item, index) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <li key={item.id}>
                {header && (
                  <p className="px-3 pt-3 pb-1 text-xs uppercase tracking-wide opacity-50">{header}</p>
                )}
                <button
                  type="button"
                  id={item.id}
                  role="option"
                  aria-selected={index === clampedActive}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left ${
                    index === clampedActive ? "bg-base-300" : "hover:bg-base-200"
                  }`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => void item.run()}
                >
                  <span className="truncate">{item.label}</span>
                  {item.hint && <span className="ml-auto text-xs opacity-50 whitespace-nowrap">{item.hint}</span>}
                </button>
              </li>
            );
          })}

          {!searchQuery.isFetching && items.length === 0 && (
            <li className="px-3 py-6 text-center text-sm opacity-60">
              No matches. Try a run number, a section id, or "excess solar".
            </li>
          )}
        </ul>

        <div className="flex items-center gap-3 border-t border-base-300 px-3 py-2 text-xs opacity-60">
          {status ? <span>{status}</span> : <span>↑↓ to move · Enter to open · Esc to close</span>}
        </div>
      </div>
    </div>
  );
}
